import React, { useState, useEffect } from "react";
import { styled } from "@mui/system";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
} from "@mui/material";
import TimeSlotButton from "./TimeSlotButton";
import moment from "moment";
import { getCourts, getPadelBookings, getTimeTable } from "../services/API";
import { timesUntil17 } from "../constants/timeTable";

const StyledTableCell = styled(TableCell)`
  && {
    font-weight: bold;
    text-align: center;
    padding: 8px 4px;
    border: 1px solid rgba(224, 224, 224, 1);
    min-width: 85px;

    @media (max-width: 600px) {
      font-size: 12px;
      padding: 6px 4px;
    }
  }
`;

const TableWrapper = styled(TableContainer)`
  && {
    overflow-x: auto;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;
    margin-bottom: 20px;
  }
`;

const FixedColumnCell = styled(TableCell)`
  && {
    position: sticky;
    left: 0;
    z-index: 2;
    background-color: #f5f5f5;
    font-weight: bold;
    min-width: 90px;
    border-right: 2px solid rgba(224, 224, 224, 1);
    white-space: nowrap;
    text-align: center;

    @media (max-width: 600px) {
      font-size: 12px;
      padding: 6px 4px;
    }
  }
`;

const PadelTable = ({ date, view }) => {
  const currentTime = moment().format("HH:mm");
  const currentDay = moment().format("YYYY-MM-DD");
  const [timeTable, setTimeTable] = useState([]);
  const [filteredTimeTable, setFilteredTimeTable] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [bookings, setBookings] = useState([]);

  const isStartTimeUntil17 = (startTime) => {
    return timesUntil17.some((time) => time.start === startTime);
  };

  useEffect(() => {
    if (date === currentDay) {
      const filtered = timeTable.filter((time) => time.start > currentTime);
      setFilteredTimeTable(filtered);
    } else {
      setFilteredTimeTable(timeTable);
    }
  }, [date, currentDay, currentTime, timeTable]);

  useEffect(() => {
    const fetchCourts = async () => {
      const timeTable = await getTimeTable();
      setTimeTable(timeTable);
      const courts = await getCourts(date, "Padelis");
      setCourts(courts);
      const bookings = await getPadelBookings();
      setBookings(bookings);
      setLoaded(true);
    };
    fetchCourts();
  }, [view, date]);

  const getAvailability = (courtId, date, id, start, end, duration) => {
    const court = courts.find((court) => court.courtId === courtId);
    if (!court) {
      return "Invalid court";
    }
    const courtBookings = bookings.filter(
      (booking) =>
        booking.courtId === courtId &&
        booking.date === date &&
        booking.end > start &&
        booking.start < end
    );
    if (courtBookings.length === 0) {
      return (
        <TimeSlotButton
          timeSlot={{
            courtId,
            date,
            courtName: court.courtName,
            price: isStartTimeUntil17(start)
              ? court.priceUntil17
              : court.priceAfter17,
            timeId: id,
            start,
            end,
            duration,
            sport: court.sport,
          }}
        />
      );
    } else {
      return (
        <Button
          variant="outlined"
          disabled
          sx={{
            "&.Mui-disabled": {
              backgroundColor: "#fff",
              color: "#156596",
            },
          }}
        >
          X
        </Button>
      );
    }
  };

  if (!loaded) {
    return null;
  }

  return (
    <TableWrapper>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <FixedColumnCell>Laikas</FixedColumnCell>
            {courts.map((court) => (
              <StyledTableCell
                key={court.courtId}
                sx={{
                  backgroundColor: "#f5f5f5",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: "85px", sm: "120px" },
                }}
              >
                {court.courtName}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTimeTable.map((time) => (
            <TableRow
              key={time.start}
              sx={{
                "&:nth-of-type(odd)": {
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            >
              <FixedColumnCell>{`${time.start} - ${time.end}`}</FixedColumnCell>
              {courts.map((court) => (
                <TableCell
                  sx={{
                    textAlign: "center",
                    padding: { xs: "6px 4px", sm: "8px 6px" },
                    border: "1px solid rgba(224, 224, 224, 1)",
                    minWidth: { xs: "85px", sm: "120px" },
                  }}
                  key={court.courtId}
                >
                  {getAvailability(
                    court.courtId,
                    date,
                    time.id,
                    time.start,
                    time.end,
                    time.duration,
                    court.sport
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};

export default PadelTable;
